
import {
  createWritableDir,
  deleteWritableDir,
  listWritableDirs,
  type WritableDir,
} from "../api/settings.js";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function dirView(dir: WritableDir): { id: string; name: string; meta: string } {
  return {
    id: dir.id,
    name: escapeHtml(dir.path.startsWith("/") ? dir.path : `/${dir.path}`),
    meta: "Writable by your agents, including every subdirectory.",
  };
}

async function submitWritableDir(form: HTMLFormElement): Promise<void> {
  const data = new FormData(form);
  await createWritableDir(String(data.get("path") ?? "").trim());
}

const styles = css`
  .account-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .account {
    align-items: center;
    border: 1px solid var(--border);
    border-radius: 10px;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    padding: 12px 14px;
  }

  .name {
    color: var(--foreground);
    font-size: 14px;
    font-weight: 600;
  }

  .meta,
  .muted {
    color: var(--muted-foreground);
    font-size: 13px;
    line-height: 1.5;
  }

  form {
    display: grid;
    gap: 14px;
  }

  label {
    color: var(--foreground);
    display: grid;
    font-size: 13px;
    font-weight: 500;
    gap: 6px;
  }

  input {
    background: var(--background);
    border: 1px solid var(--input);
    border-radius: 8px;
    box-sizing: border-box;
    color: var(--foreground);
    font: inherit;
    font-size: 14px;
    height: 36px;
    outline: none;
    padding: 8px 10px;
    width: 100%;
  }

  input:focus {
    border-color: var(--ring);
    box-shadow: 0 0 0 3px var(--ring-shadow);
  }

  .actions app-button,
  .account app-button {
    width: auto;
  }

  .error {
    color: var(--destructive);
    font-size: 13px;
    margin: 8px 0 0;
  }

  .error:empty {
    display: none;
  }
`;

export class AppSettingsFiles extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSettingsFiles)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = [];

  #dirs;
  #loaded;
  #error;
  
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #eventContexts = {};
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  #setDirs(value) {
    if (Object.is(this.#dirs, value)) return;
    this.#dirs = value;
    this.#updateBindings('dirs');
  }

  #setLoaded(value) {
    if (Object.is(this.#loaded, value)) return;
    this.#loaded = value;
    this.#updateBindings('loaded');
  }

  #setError(value) {
    if (Object.is(this.#error, value)) return;
    this.#error = value;
    this.#updateBindings('error');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;

  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;

      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    void listWritableDirs()
        .then((items) => {
        this.#setDirs(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to load writable directories.");
    });
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }


  #initState() {
    this.#dirs = [];
    this.#loaded = false;
    this.#error = "";
  }

  #render() {
    const dirViews = this.#dirs.map(dirView);
    return `<style>${this.#escText(styles)}</style><app-settings-section title="Writable folders" description="By default your agents may only write inside a thread's workspace or its project folder. Add personal folders here to let agents create and edit files in them. Every subfolder is included."><!--argon-text:0-->${(dirViews.length > 0) ? `<div class="account-list">${dirViews.map((dir, __argonIndex) => this.#mapEventItem(`<div class="account"><div><div class="name">${dir.name}</div><div class="meta">${dir.meta}</div></div><app-button variant="outline" size="sm" data-argon="3291">Remove</app-button></div>`, "initial:1:3291:" + __argonIndex, { 3291: () => {
    if (!window.confirm("Revoke write access to this directory?"))
        return;
    void deleteWritableDir(dir.id)
        .then(() => listWritableDirs())
        .then((items) => {
        this.#setDirs(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to remove directory.");
    });
} }, ["3291"])).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No writable folders yet. Agents can still write to the thread workspace and project folders." : "Loading folders...")}</p>`}<!--/argon-text:0--></app-settings-section><app-settings-section title="Add writable folder" description="Enter a path relative to your files, e.g. Documents or Notes/Personal. The folder and everything under it becomes writable."><form data-argon="4428"><label>Folder path<input name="path" required placeholder="Documents/Notes" autocomplete="off"></label><div class="actions"><app-button type="submit">Add folder</app-button></div><p class="error"><!--argon-text:1-->${this.#escText(this.#error)}<!--/argon-text:1--></p></form></app-settings-section>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      this.#eventHandler(target, id, handler).call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #mapEventItem(html, ctx, handlers, ids) {
    const key = String(ctx);
    this.#eventContexts[key] = handlers;
    const attr = key.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    let out = String(html);
    for (const id of ids) out = out.replaceAll(`data-argon="${id}"`, `data-argon="${id}" data-argon-c="${attr}"`);
    return out;
  }

  #eventHandler(el, id, fallback) {
    const ctx = el.getAttribute('data-argon-c');
    return ctx && this.#eventContexts[ctx]?.[id] ? this.#eventContexts[ctx][id] : fallback;
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const dirViews = this.#dirs.map(dirView);
    if (prop === undefined || prop === 'dirs' || prop === 'loaded') {
      const __argonNext = `${(dirViews.length > 0) ? `<div class="account-list">${dirViews.map((dir, __argonIndex) => this.#mapEventItem(`<div class="account"><div><div class="name">${dir.name}</div><div class="meta">${dir.meta}</div></div><app-button variant="outline" size="sm" data-argon="3291">Remove</app-button></div>`, "0:1:3291:" + __argonIndex, { 3291: () => {
    if (!window.confirm("Revoke write access to this directory?"))
        return;
    void deleteWritableDir(dir.id)
        .then(() => listWritableDirs())
        .then((items) => {
        this.#setDirs(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to remove directory.");
    });
} }, ["3291"])).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No writable folders yet. Agents can still write to the thread workspace and project folders." : "Loading folders...")}</p>`}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'error') {
      const __argonNext = `${this.#escText(this.#error)}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '4428').forEach(el => this.#listen(el, '4428', 'submit', (event: Event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    this.#setError("");
    void submitWritableDir(form)
        .then(() => {
        form.reset();
        return listWritableDirs();
    })
        .then((items) => {
        this.#setDirs(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch((err) => {
        this.#setError(err instanceof Error ? err.message : "Failed to add directory.");
    });
}));
    this.#eventTargets(scope, '3291').forEach(el => this.#listen(el, '3291', 'click', () => {
    if (!window.confirm("Revoke write access to this directory?"))
        return;
    void deleteWritableDir(dir.id)
        .then(() => listWritableDirs())
        .then((items) => {
        this.#setDirs(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to remove directory.");
    });
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
